import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useRequest from "../../utils/useRequest";

// Utils
import { UserAuth } from "../../utils/AuthContext";
import { formatDate } from "../../utils/toDate";
import { sanitizeNumber } from "../../utils/sanitizeNumber";

// Icons
import searchIcon from "../../assets/search_icon.png";

// Components
import PageWrapper from "../../components/PageWrapper";
import CustomInput from "../../components/CustomInput";
import SearchFilter from "../../components/SearchFilter";
import CustomButton from "../../components/CustomButton";
import InformationBox from "../../components/InformationBox";

import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../components/table-component/RoundedTable";
import Loader from "../../components/Loader";
import PageHeadline from "../../components/PageHeadline";

export default function PreparedOrders() {
  const { t } = useTranslation("general");

  const { userData } = useContext(UserAuth);

  const { loading, error, data, refetch } = useRequest(
    `/getFabricSellInvoices`,
    "GET",
    null,
    {
      organizationId: userData?.organizationId,
      page: 1,
      limit: 5,
      status: "PREPARED",
    },
    false
  );

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  // Search states
  const [selectedTitle, setSelectedTitle] = useState({
    title: t("invoiceNumber"),
    value: "id",
  });

  const [searchQuery, setSearchQuery] = useState(null);
  const [startDate, setStartDate] = useState(formatDate(new Date()));
  const [endDate, setEndDate] = useState(formatDate(new Date()));

  useEffect(() => {
    refetch({
      page: pagination.currentPage,
      limit: 5,
      searchQuery,
      searchType: selectedTitle.value,
    });
  }, [pagination.currentPage]);

  useEffect(() => {
    const orderLists = data;
    if (orderLists) {
      setPagination({
        currentPage: data?.pagination?.currentPage,
        totalPages: data?.pagination?.totalPages,
        totalRecords: data?.pagination?.totalCount,
      });
    }
  }, [data]);

  const tableTitles = [
    {
      value: "invoiceNumber",
    },
    {
      value: "Date",
      keepOnMobile: true,
    },
  ];

  const tableRows = data?.data?.map((el) => {
    const transactionName = el.id;
    return [
      {
        value: transactionName,
        type: TABLE_CELL_TYPES.LINK,

        to: {
          pathname: `/pending-orders/paper-invoice/${el.id}`,
        },
        bold: true,
        keepOnMobile: true,
      },
      { value: el.date, type: TABLE_CELL_TYPES.DATE, keepOnMobile: true },
    ];
  });

  const handleSearchType = async (type) => {
    if (type.value === "invoiceNumber" || type.value === "amount") {
      setStartDate(formatDate(new Date()));
      setEndDate(formatDate(new Date()));
    } else if (type.value === "date") {
      setSearchQuery("");
    } else {
      console.error("invalid search type");
    }

    setSelectedTitle(type);
  };

  const handleSearch = async (query = searchQuery) => {
    if (typeof query !== "string") {
      query = searchQuery;
    }

    let searchBy;

    if (selectedTitle?.value === "date") {
      searchBy = { from: startDate, to: endDate };
    } else if (selectedTitle?.value === "amount") {
      searchBy = sanitizeNumber(query);
    } else {
      searchBy = query;
    }

    refetch({
      searchQuery: searchBy,
      searchType: selectedTitle.value,
      startDate: selectedTitle?.value === "date" ? startDate : "",
      endDate: selectedTitle?.value === "date" ? endDate : "",
    });
  };

  return (
    <PageWrapper withBackButton>
      <div className="o-column o-column--gap-size_500 u-margin--top_500">
        <PageHeadline title="completedOrders" classList="u-margin--top_400" />

        {data?.length !== 0 ? (
          <section className="o-column-row-switcher o-row--gap-size_300 o-column--vertical-align_end">
            <div className="o-row o-row--vertical-align_center o-column--vertical-align_end o-row--gap-size_300 u-width_full-width o-column--horizontal-align_space-between">
              {selectedTitle.value === "date" ? (
                <div className="o-column-row-switcher o-column--gap-size_300 ">
                  <CustomInput
                    name="date"
                    title={t("from")}
                    type="date"
                    thick
                    onChange={(date) => {
                      setStartDate(date?.target?.value);
                    }}
                    value={formatDate(startDate)}
                  />

                  <CustomInput
                    name="date"
                    title={t("to")}
                    type="date"
                    thick
                    onChange={(date) => {
                      setEndDate(date?.target?.value);
                    }}
                    value={formatDate(endDate)}
                  />
                </div>
              ) : (
                <CustomInput
                  placeholder={`${t("searchTitle")} ${t(selectedTitle?.title)}`}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  resetSearch={() => {
                    setSearchQuery(null);
                    handleSearch("");
                  }}
                  thick
                  flex
                />
              )}
              <SearchFilter
                options={[
                  {
                    title: t("invoiceNumber"),
                    value: "id",
                  },
                  { title: t("Date"), value: "date" },
                ]}
                selected={selectedTitle}
                setSelected={handleSearchType}
                thick
              />
            </div>

            <CustomButton
              title={t("search")}
              onClick={handleSearch}
              icon={searchIcon}
              iconSize={15}
              classList="c-search-button"
            />
          </section>
        ) : null}

        {loading && <Loader />}

        {tableRows?.length ? (
          <section>
            <RoundedTable
              titles={tableTitles}
              rows={tableRows}
              pagination={pagination}
              setPagination={setPagination}
            />
          </section>
        ) : (
          <InformationBox>{t("noOrders")}</InformationBox>
        )}

        {error && (
          <InformationBox>
            {t("error")} {error}
          </InformationBox>
        )}
      </div>
    </PageWrapper>
  );
}
